import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CardTypesEnum, Heading, useResponsive } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { ErrorBoundary } from '@/modules/core/components';
import { useBreadcrumbs } from '@/modules/core/hooks';
import { useOrphanModeStore } from '@/modules/core/store';
import { CIPBanner } from '@/modules/investments/cip';
import { useCIPInfo } from '@/modules/investments/cip/hooks';
import useIsFullLiquidation from '@/modules/investments/hooks/useIsFullLiquidation';
import { useMyEnrollments } from '@/modules/transaction/hooks';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';

import FundsManagementBanner, { useFundManagementOptions } from '../../FundsManagementBanner';

import { CategorySpendLimitsTable } from './CategorySpendLimitsTable/CategorySpendLimitsTable';
import { EnrollmentDetailsToggleOptions, useSpendLimitsToggle } from './hooks/useSpendLimitsToggle';
import { SpendLimitsTable } from './SpendLimitsTable/SpendLimitsTable';
import AccountDetails from './AccountDetails';
import ActivitiesList from './ActivitiesList';
import DisclosureBankMessage from './DisclosureBankMessage';
import DisclosureButton from './DisclosureButton';
import { EnrollmentDetailsToggler } from './EnrollmentDetailsToggler';
import { MoneyMovedMessage } from './MoneyMovedMessage';
import { Wrapper } from './MyAccounts.styles';

interface Props {
  accountId: string;
}
const MyAccounts: React.FC<Props> = ({ accountId }) => {
  const { t } = useTranslation();

  const {
    currentMyAccount,
    isCurrentHSA,
    isGenerateStatementsEnabled,
    breadcrumbAccountTitle,
    isOpenHSAExist,
  } = useMyEnrollments(accountId);
  const {
    selectedOption,
    setSelectedOption,
    toggleOptions,
    hasSpendLimits,
    showCategorySpendLimits,
    spendLimitsData,
    isLoading: isSpendLimitsLoading,
    showPeriodDropdown,
    periodOptions,
    selectedPeriod,
    handlePeriodChange,
    isPeriodLoading,
    categorySpendLimitsData,
    isCategorySpendLimitsLoading,
    showCategoryPeriodDropdown,
    categoryPeriodOptions,
    categorySelectedPeriod,
    handleCategoryPeriodChange,
    isCategoryPeriodLoading,
  } = useSpendLimitsToggle({
    accountId,
  });

  useBreadcrumbs(
    [
      {
        key: ROUTES.HOME,
        title: t('Home'),
        value: ROUTES.HOME,
      },
      {
        key: ROUTES.MY_ACCOUNTS,
        title: t('My accounts'),
        value: `${ROUTES.MY_ACCOUNTS}`,
      },
      {
        key: ROUTES.MY_ACCOUNTS_DETAILS(accountId),
        title: breadcrumbAccountTitle,
        value: ROUTES.MY_ACCOUNTS_DETAILS(accountId),
      },
    ],
    true
  );
  const { isVerificationRequired } = useCIPInfo();
  const isNotVerified = useMemo(
    () => isVerificationRequired && currentMyAccount?.isCIPRequired,
    [currentMyAccount, isVerificationRequired]
  );
  const { employee } = useEmployeeStore();
  const { linkedEmployee } = useOrphanModeStore();
  const { isFullLiquidated } = useIsFullLiquidation({ accountId });
  const { fundManagementItems, fundManagementButtons } = useFundManagementOptions(currentMyAccount, {
    accountId,
    isNotVerified,
    isFullLiquidation: isFullLiquidated,
  });
  const { isMobile: isMobileMode } = useResponsive();
  const hasMoneyMovedMessage = useMemo(
    () =>
      Boolean(
        employee &&
        employee?.isOrphaned &&
        linkedEmployee &&
        currentMyAccount?.accountType === CardTypesEnum.HSA
      ),
    [employee]
  );
  return (
    <Wrapper data-testid="MyAccounts-wrapper">
      {currentMyAccount && (
        <Box margin={{ bottom: 'spacings24' }}>
          <Box
            direction="row"
            align="center"
            justify="between"
            margin={{ horizontal: isMobileMode ? 'spacing16' : '' }}
          >
            <Heading color="textTitle" size={isMobileMode ? 'mobile' : 'regular'}>
              {currentMyAccount.name}
            </Heading>
            <div>
              {(isGenerateStatementsEnabled || !!fundManagementButtons.length) && (
                <DisclosureButton
                  isOpenHSAExist={isOpenHSAExist}
                  externalButtons={fundManagementButtons}
                  accountId={accountId}
                  isCurrentHSA={isCurrentHSA}
                />
              )}
            </div>
          </Box>
          {hasMoneyMovedMessage ? (
            <Box margin={{ top: 'spacing24' }}>
              <MoneyMovedMessage />
            </Box>
          ) : null}
          <AccountDetails id={accountId} currentMyAccount={currentMyAccount} />
        </Box>
      )}
      {isNotVerified && (
        <Box pad={{ bottom: 'l' }}>
          <CIPBanner />
        </Box>
      )}
      {isCurrentHSA && (
        <FundsManagementBanner accountId={accountId} fundsManagementItems={fundManagementItems} />
      )}

      {currentMyAccount && toggleOptions.length > 1 ? (
        isMobileMode ? (
          <Box background="module" round="container3Round" pad="spacing16">
            <EnrollmentDetailsToggler
              toggleOptions={toggleOptions}
              selectedOption={selectedOption}
              setSelectedOption={(value: string) =>
                setSelectedOption(value as EnrollmentDetailsToggleOptions)}
            />
            {selectedOption === EnrollmentDetailsToggleOptions.ACTIVITIES && (
              <ErrorBoundary>
                <ActivitiesList
                  disabled={isNotVerified}
                  key={accountId}
                  accountId={accountId}
                  title={t('Activity')}
                  hideBackground
                />
              </ErrorBoundary>
            )}
            {selectedOption === EnrollmentDetailsToggleOptions.SPEND_LIMITS && hasSpendLimits && (
              <SpendLimitsTable
                dependents={spendLimitsData}
                isLoading={isSpendLimitsLoading}
                hideBackground
                showPeriodDropdown={showPeriodDropdown}
                periodOptions={periodOptions}
                selectedPeriod={selectedPeriod}
                onPeriodChange={handlePeriodChange}
                isPeriodLoading={isPeriodLoading}
              />
            )}
            {selectedOption === EnrollmentDetailsToggleOptions.SPEND_LIMITS && showCategorySpendLimits && (
              <CategorySpendLimitsTable
                categories={categorySpendLimitsData}
                isLoading={isCategorySpendLimitsLoading}
                hideBackground
                showPeriodDropdown={showCategoryPeriodDropdown}
                periodOptions={categoryPeriodOptions}
                selectedPeriod={categorySelectedPeriod}
                onPeriodChange={handleCategoryPeriodChange}
                isPeriodLoading={isCategoryPeriodLoading}
              />
            )}
          </Box>
        ) : (
          <>
            <EnrollmentDetailsToggler
              toggleOptions={toggleOptions}
              selectedOption={selectedOption}
              setSelectedOption={(value: string) =>
                setSelectedOption(value as EnrollmentDetailsToggleOptions)}
            />
            {selectedOption === EnrollmentDetailsToggleOptions.ACTIVITIES && (
              <ErrorBoundary>
                <ActivitiesList
                  disabled={isNotVerified}
                  key={accountId}
                  accountId={accountId}
                  title={t('Activity')}
                />
              </ErrorBoundary>
            )}
            {selectedOption === EnrollmentDetailsToggleOptions.SPEND_LIMITS && hasSpendLimits && (
              <SpendLimitsTable
                dependents={spendLimitsData}
                isLoading={isSpendLimitsLoading}
                showPeriodDropdown={showPeriodDropdown}
                periodOptions={periodOptions}
                selectedPeriod={selectedPeriod}
                onPeriodChange={handlePeriodChange}
                isPeriodLoading={isPeriodLoading}
              />
            )}
            {selectedOption === EnrollmentDetailsToggleOptions.SPEND_LIMITS && showCategorySpendLimits && (
              <CategorySpendLimitsTable
                categories={categorySpendLimitsData}
                isLoading={isCategorySpendLimitsLoading}
                showPeriodDropdown={showCategoryPeriodDropdown}
                periodOptions={categoryPeriodOptions}
                selectedPeriod={categorySelectedPeriod}
                onPeriodChange={handleCategoryPeriodChange}
                isPeriodLoading={isCategoryPeriodLoading}
              />
            )}
          </>
        )
      ) : (
        currentMyAccount &&
        selectedOption === EnrollmentDetailsToggleOptions.ACTIVITIES && (
          <ErrorBoundary>
            <ActivitiesList
              disabled={isNotVerified}
              key={accountId}
              accountId={accountId}
              title={t('Activity')}
            />
          </ErrorBoundary>
        )
      )}
      <DisclosureBankMessage isCurrentHSA={isCurrentHSA} />
    </Wrapper>
  );
};

export default MyAccounts;
